import type { U3129FindTasksForProjectRequest, U3129HonorariumItem, U3129Timesheet } from "../U3129Service";
import {
  u3129InvalidateFindTasksForProjectCache,
  type U3129FindTasksForProjectResult,
} from "../U3129UseFindProjectSubprojectHandlers";
import {
  u3129BuildHonorariumCoherenceWarnings,
  u3129EmptyHonorariumCoherenceWarnings,
  u3129UniqueFindTasksBodiesFromHonorariumRows,
  type U3129HonorariumCoherenceMessages,
  type U3129HonorariumCoherenceWarningsState,
} from "./u3129HonorariumCoherenceWarnings";
import type { U3129HonorariumTemplateScopeResolution } from "./u3129HonorariumTemplate";

export async function u3129ResolveHonorariumCoherenceWarnings(
  data: U3129Timesheet | null | undefined,
  employeeId: string | undefined,
  fetchTasksResult: (
    body: U3129FindTasksForProjectRequest,
    options?: { silent?: boolean },
  ) => Promise<U3129FindTasksForProjectResult>,
  messages: U3129HonorariumCoherenceMessages,
  admIncludeSubProjectEnabled: boolean,
): Promise<U3129HonorariumCoherenceWarningsState> {
  const rows = data?.honoraria ?? [];
  if (rows.length === 0) {
    return u3129EmptyHonorariumCoherenceWarnings();
  }

  const scopes = u3129UniqueFindTasksBodiesFromHonorariumRows(rows);
  const resolutions = new Map<string, U3129HonorariumTemplateScopeResolution>();

  await Promise.all(
    scopes.map(async ({ scopeKey, body }) => {
      // Ne pas réutiliser le cache des tâches (projet peut être fermé depuis le dernier chargement).
      u3129InvalidateFindTasksForProjectCache(scopeKey);
      const result = await fetchTasksResult(
        {
          projectId: body.projectId,
          subprojectId: body.subprojectId,
          employeeId: employeeId?.trim() || undefined,
        },
        { silent: true },
      );
      if (!result.ok) {
        resolutions.set(scopeKey, { status: "unusable" });
        return;
      }
      const taskIds = new Set(result.tasks.map((t) => t.id).filter((id): id is string => !!id));
      resolutions.set(scopeKey, { status: "usable", taskIds });
    }),
  );

  return u3129BuildHonorariumCoherenceWarnings(rows, resolutions, messages, admIncludeSubProjectEnabled);
}
